import { useState, useCallback, useMemo, useRef } from 'react'
import { AgGridReact } from 'ag-grid-react'
import { RowClassParams } from 'ag-grid-community'
import { BulkSheetView } from '../../containers/BulkSheetView'
import { UserApiTokenRow } from './UserApiTokenRow'
import { useGridOptions } from './gridOptions/useGridOptions'
import { createGetContextMenuItems } from './gridOptions/contextMenu'
import { useUserApiTokenData } from './hooks/useUserApiTokenData'
import UserApiTokensHeader from './components/Header'
import CreateTokenDialog from './components/CreateTokenDialog'
import TokenDisplayModal from './components/TokenDisplayModal'
import RevokeConfirmDialog from './components/RevokeConfirmDialog'
import Loading from '../../components/process-state-notifications/Loading'
import { colorPalette } from '../../style/colorPalette'
import { PageArea } from '..'

type Props = {
  uuid: string
}

const UserApiTokens = ({ uuid: functionUuid }: Props) => {
  const bulkSheetRef = useRef<HTMLDivElement>(null)
  const gridRef = useRef<AgGridReact<UserApiTokenRow>>(null)
  const [createDialogOpen, setCreateDialogOpen] = useState(false)
  const [tokenModalOpen, setTokenModalOpen] = useState(false)
  const [displayedToken, setDisplayedToken] = useState<string | null>(null)
  const [revokeDialogOpen, setRevokeDialogOpen] = useState(false)
  const [revokeTarget, setRevokeTarget] = useState<UserApiTokenRow | null>(null)

  const {
    rowData,
    availableScopes,
    loading,
    onReload,
    onCreateToken,
    onRevokeToken,
  } = useUserApiTokenData(functionUuid)

  const gridOptions = useGridOptions()

  const onRevokeClick = useCallback((row: UserApiTokenRow) => {
    setRevokeTarget(row)
    setRevokeDialogOpen(true)
  }, [])

  const onConfirmRevoke = useCallback(async () => {
    if (!revokeTarget) return
    const success = await onRevokeToken(revokeTarget.uuid)
    if (success) {
      setRevokeDialogOpen(false)
      setRevokeTarget(null)
    }
  }, [revokeTarget, onRevokeToken])

  const onCancelRevoke = useCallback(() => {
    setRevokeDialogOpen(false)
    setRevokeTarget(null)
  }, [])

  const getContextMenuItems = useMemo(
    () => createGetContextMenuItems(onRevokeClick),
    [onRevokeClick]
  )

  const onOpenCreateDialog = useCallback(() => {
    setCreateDialogOpen(true)
  }, [])

  const onCloseCreateDialog = useCallback(() => {
    setCreateDialogOpen(false)
  }, [])

  const onSubmitCreate = useCallback(
    async ({
      tokenName,
      expiredAt,
      scopes,
    }: {
      tokenName: string
      expiredAt: number
      scopes: string[]
    }) => {
      const rawToken = await onCreateToken({
        tokenName,
        expiredAt,
        scopes,
      })
      if (rawToken) {
        setCreateDialogOpen(false)
        setDisplayedToken(rawToken)
        setTokenModalOpen(true)
      }
    },
    [onCreateToken]
  )

  const onCloseTokenModal = useCallback(() => {
    setTokenModalOpen(false)
    setDisplayedToken(null)
  }, [])

  const getRowStyle = useCallback(
    ({ data }: RowClassParams<UserApiTokenRow>) => {
      if (data?.revoked || data?.expired) {
        return { backgroundColor: colorPalette.monotone[2] }
      }
      return undefined
    },
    []
  )

  return (
    <PageArea>
      <UserApiTokensHeader
        loading={loading}
        onReload={onReload}
        onClickCreateToken={onOpenCreateDialog}
      />
      <BulkSheetView
        ref={bulkSheetRef}
        gridRef={gridRef}
        gridOptions={gridOptions}
        rowData={rowData}
        getContextMenuItems={getContextMenuItems}
        getRowStyle={getRowStyle}
      />
      <Loading isLoading={loading} elem={bulkSheetRef.current} />
      <CreateTokenDialog
        open={createDialogOpen}
        onClose={onCloseCreateDialog}
        onSubmit={onSubmitCreate}
        loading={loading}
        availableScopes={availableScopes}
      />
      <TokenDisplayModal
        open={tokenModalOpen}
        rawToken={displayedToken}
        onClose={onCloseTokenModal}
      />
      <RevokeConfirmDialog
        open={revokeDialogOpen}
        tokenName={revokeTarget?.tokenName ?? ''}
        onConfirm={onConfirmRevoke}
        onCancel={onCancelRevoke}
        loading={loading}
      />
    </PageArea>
  )
}

export default UserApiTokens
